// components/ai/AIUsageAnalytics.tsx
// Deep-dive AI usage analytics with filtering and multiple chart views

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fetchAIUsageAnalytics, getModelDistribution, getCostBreakdown } from '../../services/aiUsageService';
import { AIFeatureCategoryInfo, AIFeatureCategory } from '../../types/AIUsageTypes';
import type { AIUsageAggregatedAnalytics } from '../../types/AIUsageTypes';
import './AIUsageAnalytics.css';

export const AIUsageAnalytics: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser, signOut } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [analytics, setAnalytics] = useState<AIUsageAggregatedAnalytics | null>(null);

    // Filter state
    const [startDate, setStartDate] = useState<string>(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [selectedCategories, setSelectedCategories] = useState<AIFeatureCategory[]>([]);
    const [selectedModels, setSelectedModels] = useState<string[]>([]);
    const [selectedDevice, setSelectedDevice] = useState<string>('all');


    const loadAnalytics = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const data = await fetchAIUsageAnalytics(startDate, endDate);
            setAnalytics(data);
        } catch (err: any) {
            console.error('Error loading analytics:', err);
            setError(err.message || 'Failed to load analytics');
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        loadAnalytics();
    }, [loadAnalytics]);

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (error) {
            console.error('Failed to sign out:', error);
        }
    };

    const toggleCategory = (cat: AIFeatureCategory) => {
        setSelectedCategories(prev =>
            prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
        );
    };

    const toggleModel = (model: string) => {
        setSelectedModels(prev =>
            prev.includes(model) ? prev.filter(m => m !== model) : [...prev, model]
        );
    };

    // ── Derived / filtered data ──
    const modelDist = analytics ? getModelDistribution(analytics) : [];
    const costBreakdown = analytics ? getCostBreakdown(analytics) : [];

    const filteredModelDist = selectedModels.length > 0
        ? modelDist.filter(d => selectedModels.includes(d.label))
        : modelDist;

    // Stacked bar data: daily calls by category
    const stackedData = analytics?.dailyStats.map(day => ({
        date: day.date,
        total: day.totalCalls,
        byCategory: day.callsByCategory,
    })) || [];

    const stackedMax = stackedData.reduce((max, d) => Math.max(max, d.total), 1);

    // Response time trend
    const responseTrend = analytics?.dailyStats.map(day => ({
        date: day.date,
        avgMs: day.callsWithResponseTime > 0 ? day.totalResponseTimeMs / day.callsWithResponseTime : 0,
    })) || [];
    const responseTrendMax = responseTrend.reduce((max, d) => Math.max(max, d.avgMs), 1);

    // Model bar chart max
    const modelMax = filteredModelDist.reduce((max, d) => Math.max(max, d.value), 1);

    // All available models from data
    const allModels = analytics ? Object.keys(analytics.callsByModel) : [];
    // All categories
    const allCategories = Object.values(AIFeatureCategory);

    return (
        <div className="ai-analytics-container">
            <header className="ai-analytics-header">
                <div className="header-content">
                    <div>
                        <h1>AI Usage Analytics</h1>
                        <p className="header-subtitle">Deep-dive into all AI feature usage across the app</p>
                    </div>
                    <div className="header-actions">
                        <button onClick={() => navigate('/ai')} className="back-button">← AI Dashboard</button>
                        <span className="user-email">{currentUser?.email}</span>
                        <button onClick={handleSignOut} className="logout-button">Sign Out</button>
                    </div>
                </div>
            </header>

            <div className="ai-analytics-main">
                {/* Filters */}
                <section className="filters-section">
                    <div className="filter-row">
                        <div className="filter-group">
                            <label>Start Date</label>
                            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                        </div>
                        <div className="filter-group">
                            <label>End Date</label>
                            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                        </div>
                        <div className="filter-group">
                            <label>Device</label>
                            <select value={selectedDevice} onChange={e => setSelectedDevice(e.target.value)}>
                                <option value="all">All Devices</option>
                                <option value="iPhone">iPhone</option>
                                <option value="iPad">iPad</option>
                            </select>
                        </div>
                    </div>

                    <div className="filter-row chips-row">
                        <label className="chips-label">Category:</label>
                        {allCategories.map(cat => {
                            const info = AIFeatureCategoryInfo[cat];
                            const active = selectedCategories.includes(cat);
                            return (
                                <button
                                    key={cat}
                                    className={`chip ${active ? 'active' : ''}`}
                                    style={active ? { background: info.color, color: 'white' } : {}}
                                    onClick={() => toggleCategory(cat)}
                                >
                                    {info.icon} {info.displayName}
                                </button>
                            );
                        })}
                    </div>

                    <div className="filter-row chips-row">
                        <label className="chips-label">Model:</label>
                        {allModels.map(model => {
                            const active = selectedModels.includes(model);
                            return (
                                <button
                                    key={model}
                                    className={`chip ${active ? 'active model-chip' : ''}`}
                                    onClick={() => toggleModel(model)}
                                >
                                    {model}
                                </button>
                            );
                        })}
                    </div>
                </section>

                {loading && (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading analytics...</p>
                    </div>
                )}

                {error && (
                    <div className="error-state">
                        <p>{error}</p>
                        <button onClick={loadAnalytics} className="retry-button">Retry</button>
                    </div>
                )}

                {!loading && !error && analytics && (
                    <>
                        {/* Summary cards */}
                        <div className="summary-cards">
                            <div className="summary-card">
                                <div className="summary-icon">⚡</div>
                                <div className="summary-value">{analytics.totalCalls.toLocaleString()}</div>
                                <div className="summary-label">Total API Calls</div>
                            </div>
                            <div className="summary-card">
                                <div className="summary-icon">👤</div>
                                <div className="summary-value">{analytics.uniqueUsers.toLocaleString()}</div>
                                <div className="summary-label">Unique Users</div>
                            </div>
                            <div className="summary-card">
                                <div className="summary-icon">🪙</div>
                                <div className="summary-value">{analytics.totalTokens.toLocaleString()}</div>
                                <div className="summary-label">Total Tokens</div>
                            </div>
                            <div className="summary-card">
                                <div className="summary-icon">⏱️</div>
                                <div className="summary-value">{analytics.avgResponseTimeMs.toFixed(0)}ms</div>
                                <div className="summary-label">Avg Response Time</div>
                            </div>
                            <div className="summary-card">
                                <div className="summary-icon">❌</div>
                                <div className="summary-value">{analytics.failureRate.toFixed(1)}%</div>
                                <div className="summary-label">Failure Rate</div>
                            </div>
                            <div className="summary-card">
                                <div className="summary-icon">🚩</div>
                                <div className="summary-value">{analytics.flagRate.toFixed(1)}%</div>
                                <div className="summary-label">Flag Rate</div>
                            </div>
                        </div>

                        {/* Stacked bar: daily calls by category */}
                        <div className="chart-card">
                            <h3>Daily Calls by Category</h3>
                            <div className="stacked-chart">
                                {stackedData.map(day => {
                                    const heightPct = stackedMax > 0 ? (day.total / stackedMax) * 100 : 0;
                                    return (
                                        <div key={day.date} className="stacked-column">
                                            <span className="stacked-value">{day.total}</span>
                                            <div className="stacked-cell">
                                                <div className="stacked-bar" style={{ height: `${heightPct}%` }}>
                                                    {Object.entries(day.byCategory)
                                                        .sort((a, b) => b[1] - a[1])
                                                        .map(([cat, count]) => {
                                                            const segPct = day.total > 0 ? (count / day.total) * 100 : 0;
                                                            const color = (AIFeatureCategoryInfo as any)[cat]?.color || '#9ca3af';
                                                            return (
                                                                <div
                                                                    key={cat}
                                                                    className="stacked-segment"
                                                                    style={{ height: `${segPct}%`, background: color }}
                                                                    title={`${(AIFeatureCategoryInfo as any)[cat]?.displayName || cat}: ${count}`}
                                                                ></div>
                                                            );
                                                        })}
                                                </div>
                                            </div>
                                            <span className="stacked-date">{day.date.slice(5)}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Two-column: Category pie + Model bar */}
                        <div className="two-col-charts">
                            {/* Category pie */}
                            <div className="chart-card">
                                <h3>Token Consumption by Category</h3>
                                <div className="horiz-bar-list">
                                    {costBreakdown.map(item => {
                                        const totalTokens = costBreakdown.reduce((s, i) => s + i.totalTokens, 0);
                                        const pct = totalTokens > 0 ? (item.totalTokens / totalTokens) * 100 : 0;
                                        return (
                                            <div key={item.category} className="horiz-bar-item">
                                                <div className="horiz-bar-label">
                                                    <span>{item.displayName}</span>
                                                    <span className="horiz-bar-count">{item.totalTokens.toLocaleString()}</span>
                                                </div>
                                                <div className="horiz-bar-track">
                                                    <div className="horiz-bar-fill" style={{ width: `${pct}%`, background: item.color }}></div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Model bar */}
                            <div className="chart-card">
                                <h3>API Model Distribution</h3>
                                <div className="horiz-bar-list">
                                    {filteredModelDist.map(item => {
                                        const pct = modelMax > 0 ? (item.value / modelMax) * 100 : 0;
                                        return (
                                            <div key={item.label} className="horiz-bar-item">
                                                <div className="horiz-bar-label">
                                                    <span>{item.label}</span>
                                                    <span className="horiz-bar-count">{item.value.toLocaleString()}</span>
                                                </div>
                                                <div className="horiz-bar-track">
                                                    <div className="horiz-bar-fill" style={{ width: `${pct}%`, background: item.color }}></div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Response time trend */}
                        <div className="chart-card">
                            <h3>Average Response Time Trend (ms)</h3>
                            <div className="trend-chart">
                                {responseTrend.map((point) => {
                                    const heightPct = responseTrendMax > 0 ? (point.avgMs / responseTrendMax) * 100 : 0;
                                    return (
                                        <div key={point.date} className="trend-column">
                                            <span className="trend-value">{point.avgMs.toFixed(0)}</span>
                                            <div className="trend-cell">
                                                <div className="trend-bar" style={{ height: `${heightPct}%` }}></div>
                                            </div>
                                            <span className="trend-date">{point.date.slice(5)}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
